import React, { useEffect, useRef, useState } from 'react';
import * as echarts from 'echarts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import axiosInstance from "@/api/axios"

interface EChartsOption extends echarts.EChartsOption {}

interface HistoricalDataPoint {
  year: number;
  total_weight_kg: number;
}

interface ScenarioProjection {
  year: number;
  total_weight_kg: number;
}

interface Scenario {
  name: string;
  description: string;
  yearly_projections: ScenarioProjection[];
}

interface ApiResponse {
  status: string;
  data: {
    historical_data?: {
      yearly_totals: HistoricalDataPoint[];
    };
    scenarios?: {
      realistic: Scenario;
      optimistic: Scenario;
      pessimistic: Scenario;
    };
  };
}

const WasteScenariosComponent: React.FC = () => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<'5years' | '10years'>('5years');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const periodLabels = {
    "5years": "5 Years",
    "10years": "10 Years"
  };

  useEffect(() => {
    // Initialize chart when component mounts
    if (chartRef.current) {
      chartInstance.current = echarts.init(chartRef.current);
    }

    // Handle resize events for responsiveness
    const handleResize = () => {
      if (chartRef.current && chartInstance.current) {
        chartInstance.current.resize();
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (chartInstance.current) {
        chartInstance.current.dispose();
        chartInstance.current = null;
      }
    };
  }, []);

  const fetchScenarioData = async () => {
    setLoading(true);
    setError(null);

    try {
      const forecastYears = selectedPeriod === '5years' ? 5 : 10;

      // Call your enhanced API endpoint using axiosInstance
      const response = await axiosInstance.get(
        `waste_created_forecast?period=yearly&scenarios=true&include_historical=true&forecast_years=${forecastYears}&historical_days=365`
      );

      const apiData: ApiResponse = response.data;

      if (apiData.status !== 'success') {
        throw new Error('API returned error status');
      }

      // Process the data for the chart
      updateChart(apiData.data);

    } catch (err) {
      console.error('Error fetching scenario data:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');

      // Fallback to mock data if API fails
      updateChartWithMockData();
    } finally {
      setLoading(false);
    }
  };

  const updateChart = (data: any) => {
    if (!chartInstance.current) return;

    const currentYear = new Date().getFullYear();
    const forecastYears = selectedPeriod === '5years' ? 5 : 10;

    // Get historical data
    const historicalData = data.historical_data?.yearly_totals || [];

    // Get scenario data
    const scenarios = data.scenarios || {};

    // Create year range
    const startYear = Math.max(currentYear - 6, 2019); // Show last 6 years max
    const endYear = currentYear + forecastYears;

    const years = [];
    for (let year = startYear; year <= endYear; year++) {
      years.push(year);
    }

  // Prepare data series
    const historicalValues: (number | null)[] = [];
    const realisticValues: (number | null)[] = [];
    const optimisticValues: (number | null)[] = [];
    const pessimisticValues: (number | null)[] = [];

    years.forEach(year => {
      // Historical data (solid line) - only for past and current year
      if (year <= currentYear) {
        const historicalPoint = historicalData.find((h: HistoricalDataPoint) => h.year === year);
        historicalValues.push(historicalPoint ? historicalPoint.total_weight_kg : null);
      } else {
        historicalValues.push(null);
      }

      // Scenario data (dashed lines) - only for current and future years
      if (year >= currentYear) {
        const realisticPoint = scenarios.realistic?.yearly_projections?.find((p: ScenarioProjection) => p.year === year);
        const optimisticPoint = scenarios.optimistic?.yearly_projections?.find((p: ScenarioProjection) => p.year === year);
        const pessimisticPoint = scenarios.pessimistic?.yearly_projections?.find((p: ScenarioProjection) => p.year === year);

        realisticValues.push(realisticPoint ? realisticPoint.total_weight_kg : null);
        optimisticValues.push(optimisticPoint ? optimisticPoint.total_weight_kg : null);
        pessimisticValues.push(pessimisticPoint ? pessimisticPoint.total_weight_kg : null);
      } else {
        realisticValues.push(null);
        optimisticValues.push(null);
        pessimisticValues.push(null);
      }
    });

    const option: EChartsOption = {
      tooltip: {
        trigger: 'axis',
        formatter: function (params: any) {
          let tooltip = `<strong>Year ${params[0].axisValue}</strong><br/>`;
          params.forEach((param: any) => {
            if (param.value !== null && param.value !== undefined) {
              tooltip += `${param.seriesName}: ${Math.round(param.value)} kg<br/>`;
            }
          });
          return tooltip;
        },
        confine: true,
      },
      legend: {
        data: ['Historical Data', 'Current Trend', 'Waste Reduction', 'Increased Generation'],
        bottom: 10,
        textStyle: {
          fontSize: 10
        }
      },
      xAxis: {
        type: 'category',
        data: years,
        boundaryGap: false,
        axisLabel: {
          fontSize: 10,
          interval: 'auto',
          rotate: window.innerWidth < 500 ? 30 : 0,
        },
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          fontSize: 10,
          formatter: '{value} kg',
        },
      },
      grid: {
        left: '15%',
        right: '5%',
        top: '8%',
        bottom: '25%',
        containLabel: true,
      },
      series: [
        {
          name: 'Historical Data',
          type: 'line',
          data: historicalValues,
          showSymbol: false,
          lineStyle: {
            color: '#000000',
            width: 2,
            type: 'solid'
          },
          connectNulls: false,
        },
        {
          name: 'Current Trend',
          type: 'line',
          data: realisticValues,
          showSymbol: false,
          lineStyle: {
            color: '#666666',
            width: 2,
            type: 'dashed'
          },
          connectNulls: false,
        },
        {
          name: 'Waste Reduction',
          type: 'line',
          data: optimisticValues,
          showSymbol: false,
          lineStyle: {
            color: '#28a745',
            width: 2,
            type: 'dashed'
          },
          connectNulls: false,
        },
        {
          name: 'Increased Generation',
          type: 'line',
          data: pessimisticValues,
          showSymbol: false,
          lineStyle: {
            color: '#dc3545',
            width: 2,
            type: 'dashed'
          },
          connectNulls: false,
        },
      ],
    };

    chartInstance.current.setOption(option);

    // Force resize after a short delay to ensure proper rendering
    setTimeout(() => {
      chartInstance.current?.resize();
    }, 100);
  };

  const updateChartWithMockData = () => {
    if (!chartInstance.current) return;

    const currentYear = new Date().getFullYear();
    const forecastYears = selectedPeriod === '5years' ? 5 : 10;

    // Generate mock data similar to original component
    const startYear = currentYear - 6;
    const endYear = currentYear + forecastYears;
    const years = [];

    for (let year = startYear; year <= endYear; year++) {
      years.push(year);
    }

    // Mock historical data (2019-2024)
    const historicalData = [2800, 3200, 2900, 3100, 3400, 3600, 3522];
    const historicalValues = years.map(year => {
      const index = year - startYear;
      return year <= currentYear && index < historicalData.length ? historicalData[index] : null;
    });

    // Mock scenario data
    const realisticValues = years.map(year => {
      if (year < currentYear) return null;
      const yearOffset = year - currentYear;
      return Math.round(3522 * Math.pow(1.035, yearOffset)); // 3.5% growth
    });

    const optimisticValues = years.map(year => {
      if (year < currentYear) return null;
      const yearOffset = year - currentYear;
      return Math.round(3522 * Math.pow(0.97, yearOffset)); // 3% reduction
    });

    const pessimisticValues = years.map(year => {
      if (year < currentYear) return null;
      const yearOffset = year - currentYear;
      return Math.round(3522 * Math.pow(1.08, yearOffset)); // 8% growth
    });

    const option: EChartsOption = {
      tooltip: {
        trigger: 'axis',
        formatter: function (params: any) {
          let tooltip = `<strong>Year ${params[0].axisValue}</strong><br/>`;
          params.forEach((param: any) => {
            if (param.value !== null && param.value !== undefined) {
              tooltip += `${param.seriesName}: ${Math.round(param.value)} kg<br/>`;
            }
          });
          return tooltip;
        },
        confine: true,
      },
      legend: {
        data: ['Historical Data', 'Current Trend', 'Waste Reduction', 'Increased Generation'],
        bottom: 10,
        textStyle: {
          fontSize: 10
        }
      },
      xAxis: {
        type: 'category',
        data: years,
        boundaryGap: false,
        axisLabel: {
          fontSize: 10,
          interval: 'auto',
          rotate: window.innerWidth < 500 ? 30 : 0,
        },
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          fontSize: 10,
          formatter: '{value} kg',
        },
      },
      grid: {
        left: '15%',
        right: '5%',
        top: '8%',
        bottom: '25%',
        containLabel: true,
      },
      series: [
        {
          name: 'Historical Data',
          type: 'line',
          data: historicalValues,
          showSymbol: false,
          lineStyle: {
            color: '#000000',
            width: 2,
            type: 'solid'
          },
          connectNulls: false,
        },
        {
          name: 'Current Trend',
          type: 'line',
          data: realisticValues,
          showSymbol: false,
          lineStyle: {
            color: '#666666',
            width: 2,
            type: 'dashed'
          },
          connectNulls: false,
        },
        {
          name: 'Waste Reduction',
          type: 'line',
          data: optimisticValues,
          showSymbol: false,
          lineStyle: {
            color: '#28a745',
            width: 2,
            type: 'dashed'
          },
          connectNulls: false,
        },
        {
          name: 'Increased Generation',
          type: 'line',
          data: pessimisticValues,
          showSymbol: false,
          lineStyle: {
            color: '#dc3545',
            width: 2,
            type: 'dashed'
          },
          connectNulls: false,
        },
      ],
    };

    chartInstance.current.setOption(option);

    setTimeout(() => {
      chartInstance.current?.resize();
    }, 100);
  };

  useEffect(() => {
    fetchScenarioData();
  }, [selectedPeriod]);

  return (
    <Card className="w-full h-full flex flex-col">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 px-3 sm:px-6">
        <div className="space-y-1">
          <CardTitle className="text-base sm:text-lg">Future Waste Generation Scenarios</CardTitle>
          {loading && <p className="text-xs text-muted-foreground">Loading forecast data...</p>}
          {error && <p className="text-xs text-red-500">Using fallback data</p>}
        </div>
        <Select value={selectedPeriod} onValueChange={(value: any) => setSelectedPeriod(value)}>
          <SelectTrigger className="w-28">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="5years">5 Years</SelectItem>
            <SelectItem value="10years">10 Years</SelectItem>
          </SelectContent>
        </Select>
      </CardHeader>
      <CardContent className="px-3 sm:px-6 pt-0 pb-3 sm:pb-6 flex-grow flex flex-col">
        <div className="flex-grow min-h-[200px] sm:min-h-[300px]">
          <div ref={chartRef} className="w-full h-full" />
        </div>
      </CardContent>
    </Card>
  );
};

export default WasteScenariosComponent;